(() => {
  const STORAGE_KEY = 'brosOutdoorLuxeFavorites';
  let memoryFavorites = [];
  let lastActiveElement = null;
  let syncQueued = false;

  const readFavorites = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(parsed)) return memoryFavorites;
      memoryFavorites = parsed.filter((item) => item && item.id);
      return memoryFavorites;
    } catch (error) {
      return memoryFavorites;
    }
  };

  const writeFavorites = (items) => {
    memoryFavorites = items;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      // Some browsers can block localStorage. Keep the current tab usable with memory state.
    }
  };

  const escapeHtml = (value = '') =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

  const getProductFromButton = (button) => ({
    id: String(button.dataset.productId || '').trim(),
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '#',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
  });

  const emitUpdate = () => {
    document.dispatchEvent(new CustomEvent('favorites:updated', { detail: { items: readFavorites() } }));
  };

  const setFavorite = (product) => {
    if (!product.id) return;
    const items = readFavorites();
    const exists = items.some((item) => item.id === product.id);
    const nextItems = exists
      ? items.filter((item) => item.id !== product.id)
      : [product, ...items.filter((item) => item.id !== product.id)];

    writeFavorites(nextItems);
    emitUpdate();
  };

  const removeFavorite = (productId) => {
    writeFavorites(readFavorites().filter((item) => item.id !== String(productId)));
    emitUpdate();
  };

  const renderDrawer = (items) => {
    const drawer = document.querySelector('favorites-drawer');
    if (!drawer) return;

    const empty = drawer.querySelector('[data-favorites-empty]');
    const list = drawer.querySelector('[data-favorites-list]');
    const clearButton = drawer.querySelector('[data-favorites-clear]');
    const signature = JSON.stringify(items);

    if (empty) empty.hidden = items.length > 0;
    if (clearButton) clearButton.hidden = items.length === 0;
    if (!list || list.dataset.favoritesSignature === signature) return;

    list.dataset.favoritesSignature = signature;
    list.innerHTML = items
      .map((item) => {
        const title = escapeHtml(item.title || '商品');
        const url = escapeHtml(item.url || '#');
        const image = item.image
          ? `<a class="favorites-drawer__image-link" href="${url}" aria-label="${title}">
              <img class="favorites-drawer__image" src="${escapeHtml(item.image)}" alt="${title}" loading="lazy">
            </a>`
          : `<a class="favorites-drawer__image-placeholder" href="${url}" aria-label="${title}">画像なし</a>`;

        return `
          <article class="favorites-drawer__item" data-favorite-item="${escapeHtml(item.id)}">
            ${image}
            <div class="favorites-drawer__item-content">
              <a class="favorites-drawer__item-title" href="${url}">${title}</a>
              ${item.price ? `<p class="favorites-drawer__item-price">${escapeHtml(item.price)}</p>` : ''}
              <button type="button" class="favorites-drawer__remove" data-favorites-remove="${escapeHtml(item.id)}">
                削除
              </button>
            </div>
          </article>
        `;
      })
      .join('');
  };

  const syncFavoriteUi = () => {
    const items = readFavorites();
    const favoriteIds = new Set(items.map((item) => item.id));

    document.querySelectorAll('[data-favorite-button]').forEach((button) => {
      const product = getProductFromButton(button);
      const active = favoriteIds.has(product.id);
      const title = product.title || '商品';

      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? `お気に入りから削除: ${title}` : `お気に入りに追加: ${title}`);

      const text = button.querySelector('.favorite-button__text');
      if (text) text.textContent = active ? 'お気に入り済み' : 'お気に入り';
    });

    document.querySelectorAll('[data-favorites-count]').forEach((count) => {
      count.textContent = items.length > 99 ? '99+' : String(items.length);
      count.hidden = items.length === 0;
    });

    renderDrawer(items);
  };

  const queueSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(() => {
      syncQueued = false;
      syncFavoriteUi();
    });
  };

  const openDrawer = (trigger) => {
    const drawer = document.querySelector('favorites-drawer');
    const panel = drawer?.querySelector('[data-favorites-panel]');
    if (!drawer || !panel) return;

    lastActiveElement = trigger || document.activeElement;
    syncFavoriteUi();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    panel.focus();
  };

  const closeDrawer = () => {
    const drawer = document.querySelector('favorites-drawer');
    if (!drawer) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    if (lastActiveElement && typeof lastActiveElement.focus === 'function') {
      lastActiveElement.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const favoriteButton = event.target.closest('[data-favorite-button]');
    if (favoriteButton) {
      event.preventDefault();
      event.stopPropagation();
      setFavorite(getProductFromButton(favoriteButton));
      return;
    }

    const openButton = event.target.closest('[data-favorites-open]');
    if (openButton) {
      event.preventDefault();
      openDrawer(openButton);
      return;
    }

    if (event.target.closest('[data-favorites-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const removeButton = event.target.closest('[data-favorites-remove]');
    if (removeButton) {
      event.preventDefault();
      removeFavorite(removeButton.dataset.favoritesRemove);
      return;
    }

    if (event.target.closest('[data-favorites-clear]')) {
      event.preventDefault();
      writeFavorites([]);
      emitUpdate();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && document.querySelector('favorites-drawer.is-open')) {
      closeDrawer();
    }
  });

  document.addEventListener('favorites:updated', syncFavoriteUi);
  document.addEventListener('shopify:section:load', syncFavoriteUi);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) syncFavoriteUi();
  });

  document.addEventListener('DOMContentLoaded', () => {
    syncFavoriteUi();

    const observer = new MutationObserver(queueSync);
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
